// src/components/MeetingRoomCalendar.jsx

import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxon3Plugin from "@fullcalendar/luxon3";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  useMediaQuery,
  Chip,
  TextField,
} from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import moment from "moment-timezone";

import { getBookingsByRoom, createBooking, updateBooking } from "../services/bookingService";
import { getCurrentUser } from "../services/authService";
import { useTimezone } from "../contexts/TimezoneContext";
import EditBookingModal from "./EditBookingModal";
import { 
  addToGoogleCalendar, 
  addToOutlookCalendar, 
  addToYahooCalendar, 
  addToAppleCalendar, 
  downloadICSFile 
} from "../utils/calendarUtils";

/**
 * Check if date is in the past compared to now in organization timezone.
 */
function isDateInPast(date, orgTimeZone) {
  const now = moment.tz(orgTimeZone);
  const dateInOrgTz = moment.tz(date, orgTimeZone);
  return dateInOrgTz.isBefore(now);
}

/**
 * Check for overlap of selected range (startSelected, endSelected) with existing bookings (same day).
 */
function hasOverlap(bookings, startSelected, endSelected, orgTimeZone) {
  const selectedStartInOrgTz = moment.tz(startSelected, orgTimeZone);
  const selectedEndInOrgTz = moment.tz(endSelected, orgTimeZone);
  const startMs = selectedStartInOrgTz.valueOf();
  const endMs = selectedEndInOrgTz.valueOf();

  return bookings.some((b) => {
    // Convert booking times to organization timezone for comparison
    const bStartInOrgTz = moment.tz(b.startTime, orgTimeZone);
    const bEndInOrgTz = moment.tz(b.endTime, orgTimeZone);
    const selectedInOrgTz = moment.tz(startSelected, orgTimeZone);
    
    const bStartMs = bStartInOrgTz.valueOf();
    const bEndMs = bEndInOrgTz.valueOf();

    // Only compare if same day in organization timezone
    if (bStartInOrgTz.isSame(selectedInOrgTz, 'day')) {
      // Overlap if they intersect
      if (!(endMs <= bStartMs || startMs >= bEndMs)) {
        return true;
      }
    }
    return false;
  });
}

function MeetingRoomCalendar({
  roomId,
  refreshKey,
  chosenDate,
}) {
  const calendarRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { orgTimeZone, loading: timezoneLoading } = useTimezone();

  // org default times
  const [orgStart, setOrgStart] = useState("00:00"); // e.g. "08:00"
  const [orgEnd, setOrgEnd] = useState("24:00"); // e.g. "18:00"

  // slotMinTime/slotMaxTime for FullCalendar
  const [slotMinTime, setSlotMinTime] = useState("00:00:00");
  const [slotMaxTime, setSlotMaxTime] = useState("24:00:00");

  const [userId, setUserId] = useState(null);
  const [bookings, setBookings] = useState([]);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // Edit booking modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Action selection dialog for user's bookings
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedBookingForAction, setSelectedBookingForAction] = useState(null);

  // Calendar dialog states
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [selectedBookingForCalendar, setSelectedBookingForCalendar] = useState(null);



  // responsiveness
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Debug timezone info - FullCalendar handles current time automatically
  useEffect(() => {
    console.log('=== TIMEZONE DEBUG ===');
    console.log('Organization timezone:', orgTimeZone);
    console.log('Current actual time:', moment().format('YYYY-MM-DD HH:mm:ss'));
    console.log('Current UTC time:', moment.utc().format('YYYY-MM-DD HH:mm:ss'));
    console.log('Current org time:', moment.tz(orgTimeZone).format('YYYY-MM-DD HH:mm:ss'));
    console.log('FullCalendar timeZone prop set to:', orgTimeZone);
  }, [orgTimeZone]);

  // Load user => get default times
  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        if (user && user.organization) {
          if (user.organization.defaultStartTime) {
            setOrgStart(user.organization.defaultStartTime); // e.g. "08:00"
            setSlotMinTime(`${user.organization.defaultStartTime}:00`);
          }
          if (user.organization.defaultEndTime) {
            setOrgEnd(user.organization.defaultEndTime); // e.g. "18:00"
            setSlotMaxTime(`${user.organization.defaultEndTime}:00`);
          }
          setUserId(user._id);
        }
      })
      .catch((err) => {
        console.error("Error fetching user:", err);
      });
  }, []);

  // Fetch existing bookings
  async function fetchBookings() {
    try {
      const data = await getBookingsByRoom(roomId);
      const mapped = data.map((b) => {
        const isMyBooking = b.user?._id === userId;
        // Let FullCalendar handle timezone conversion - just pass the raw UTC dates
        return {
          id: b._id, // Include booking ID for editing
          title: isMyBooking ? `My Booking` : `Booked by ${b.user?.username || "[Unknown]"}`,
          start: new Date(b.startTime), // FullCalendar will convert to orgTimeZone
          end: new Date(b.endTime),     // FullCalendar will convert to orgTimeZone
          startTime: b.startTime,
          endTime: b.endTime,
          booking: b, // Include full booking data
          isMyBooking: isMyBooking,
          backgroundColor: isMyBooking ? "#1976d2" : "#9e9e9e", // Blue for my bookings, gray for others
          borderColor: isMyBooking ? "#1976d2" : "#9e9e9e",
          editable: isMyBooking, // Only allow editing own bookings
          extendedProps: {
            isMyBooking: isMyBooking,
            booking: b
          }
        };
      });
      setBookings(mapped);

      if (calendarRef.current) {
        const api = calendarRef.current.getApi();
        api.removeAllEvents();
        api.addEventSource(mapped);
      }
    } catch (err) {
      console.error("Failed to load bookings:", err);
    }
  }

  useEffect(() => {
    if (userId) {
      fetchBookings();
    }
    // eslint-disable-next-line
  }, [roomId, refreshKey, userId]);

  // If chosenDate changes => gotoDate
  useEffect(() => {
    if (calendarRef.current && chosenDate) {
      const api = calendarRef.current.getApi();
      api.gotoDate(chosenDate);
    }
  }, [chosenDate]);

  /**
   * Called when user drags a range on the calendar
   */
  function handleSelect(selectInfo) {
    const { start, end } = selectInfo;

    // block if date is in the past (check in organization timezone)
    if (isDateInPast(start, orgTimeZone)) {
      enqueueSnackbar("Cannot book in the past", { variant: "error" });
      return;
    }

    setSelectedRange({
      start,
      end,
      allDay: selectInfo.allDay,
    });
    setStartTime(start);
    setEndTime(end);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setSelectedRange(null);
    setStartTime(null);
    setEndTime(null);
  }

  // Handle clicking on calendar events
  function handleEventClick(clickInfo) {
    const event = clickInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    
    if (isMyBooking) {
      // Show action selection dialog for user's own bookings
      setSelectedBookingForAction(event.extendedProps.booking);
      setActionDialogOpen(true);
    } else {
      // Show read-only info for other users' bookings
      enqueueSnackbar("This booking belongs to another user", { variant: "info" });
    }
  }

  // Handle editing a booking
  async function handleEditBooking(bookingId, { startTime, endTime }) {
    try {
      await updateBooking(bookingId, { startTime, endTime });
      enqueueSnackbar("Booking updated successfully", { variant: "success" });
      fetchBookings(); // Refresh the calendar
      setEditModalOpen(false);
      setSelectedBooking(null);
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error; // Let the modal handle the error display
    }
  }

  // Close edit modal
  function closeEditModal() {
    setEditModalOpen(false);
    setSelectedBooking(null);
  }

  // Action dialog handlers
  function closeActionDialog() {
    setActionDialogOpen(false);
    setSelectedBookingForAction(null);
  }

  function handleEditBookingAction() {
    setSelectedBooking(selectedBookingForAction);
    setEditModalOpen(true);
    closeActionDialog();
  }

  function handleAddToCalendarAction() {
    setSelectedBookingForCalendar(selectedBookingForAction);
    setCalendarDialogOpen(true);
    closeActionDialog();
  }

  // Calendar dialog handlers
  function closeCalendarDialog() {
    setCalendarDialogOpen(false);
    setSelectedBookingForCalendar(null);
  }

  function handleAddToGoogleCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToGoogleCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Google Calendar:", error);
      }
    }
  }

  function handleAddToOutlookCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToOutlookCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Outlook Calendar:", error);
      }
    }
  }

  function handleAddToYahooCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToYahooCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Yahoo Calendar:", error);
      }
    }
  }

  function handleAddToAppleCalendar() {
    if (selectedBookingForCalendar) {
      try {
        addToAppleCalendar(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error adding to Apple Calendar:", error);
      }
    }
  }

  function handleDownloadICS() {
    if (selectedBookingForCalendar) {
      try {
        downloadICSFile(selectedBookingForCalendar, orgTimeZone);
        closeCalendarDialog();
      } catch (error) {
        console.error("Error downloading ICS file:", error);
      }
    }
  }

  // Handle drag and drop of events
  async function handleEventDrop(dropInfo) {
    const event = dropInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    
    if (!isMyBooking) {
      enqueueSnackbar("You can only move your own bookings", { variant: "error" });
      dropInfo.revert(); // Revert the change
      return;
    }

    // Check if new time is in the past
    if (isDateInPast(event.start, orgTimeZone)) {
      enqueueSnackbar("Cannot move booking to the past", { variant: "error" });
      dropInfo.revert();
      return;
    }

    // Check if new time is within organization work hours
    const [orgSh, orgSm] = orgStart.split(":").map(Number);
    const [orgEh, orgEm] = orgEnd.split(":").map(Number);
    
    const newStartInOrgTz = moment.tz(event.start, orgTimeZone);
    const newEndInOrgTz = moment.tz(event.end, orgTimeZone);
    
    const dayStart = newStartInOrgTz.clone().set({ hour: orgSh, minute: orgSm, second: 0 });
    const dayEnd = newStartInOrgTz.clone().set({ hour: orgEh, minute: orgEm, second: 0 });
    
    if (newStartInOrgTz.isBefore(dayStart) || newEndInOrgTz.isAfter(dayEnd)) {
      enqueueSnackbar(`Time must be within work hours: ${orgStart} - ${orgEnd}`, { variant: "error" });
      dropInfo.revert();
      return;
    }

    // Check for conflicts (excluding current booking)
    const otherBookings = bookings.filter(b => b.id !== event.id);
    if (hasOverlap(otherBookings, event.start, event.end, orgTimeZone)) {
      enqueueSnackbar("Time conflicts with another booking", { variant: "error" });
      dropInfo.revert();
      return;
    }

    // Update the booking
    try {
      await updateBooking(event.extendedProps.booking._id, {
        startTime: event.start.toISOString(),
        endTime: event.end.toISOString(),
      });
      enqueueSnackbar("Booking moved successfully", { variant: "success" });
      fetchBookings(); // Refresh to ensure consistency
    } catch (error) {
      console.error("Error moving booking:", error);
      enqueueSnackbar(error.message || "Failed to move booking", { variant: "error" });
      dropInfo.revert();
    }
  }

  // Handle resizing of events
  async function handleEventResize(resizeInfo) {
    const event = resizeInfo.event;
    const isMyBooking = event.extendedProps.isMyBooking;
    
    if (!isMyBooking) {
      enqueueSnackbar("You can only resize your own bookings", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    // Check if new time is in the past
    if (isDateInPast(event.start, orgTimeZone)) {
      enqueueSnackbar("Cannot resize booking to the past", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    // Check if new time is within organization work hours
    const [orgSh, orgSm] = orgStart.split(":").map(Number);
    const [orgEh, orgEm] = orgEnd.split(":").map(Number);
    
    const newStartInOrgTz = moment.tz(event.start, orgTimeZone);
    const newEndInOrgTz = moment.tz(event.end, orgTimeZone);
    
    const dayStart = newStartInOrgTz.clone().set({ hour: orgSh, minute: orgSm, second: 0 });
    const dayEnd = newStartInOrgTz.clone().set({ hour: orgEh, minute: orgEm, second: 0 });
    
    if (newStartInOrgTz.isBefore(dayStart) || newEndInOrgTz.isAfter(dayEnd)) {
      enqueueSnackbar(`Time must be within work hours: ${orgStart} - ${orgEnd}`, { variant: "error" });
      resizeInfo.revert();
      return;
    }

    // Check for conflicts (excluding current booking)
    const otherBookings = bookings.filter(b => b.id !== event.id);
    if (hasOverlap(otherBookings, event.start, event.end, orgTimeZone)) {
      enqueueSnackbar("Time conflicts with another booking", { variant: "error" });
      resizeInfo.revert();
      return;
    }

    // Update the booking
    try {
      await updateBooking(event.extendedProps.booking._id, {
        startTime: event.start.toISOString(),
        endTime: event.end.toISOString(),
      });
      enqueueSnackbar("Booking resized successfully", { variant: "success" });
      fetchBookings(); // Refresh to ensure consistency
    } catch (error) {
      console.error("Error resizing booking:", error);
      enqueueSnackbar(error.message || "Failed to resize booking", { variant: "error" });
      resizeInfo.revert();
    }
  }

  async function handleConfirmBooking() {
    if (!userId) {
      enqueueSnackbar("Please login first", { variant: "error" });
      closeDialog();
      return;
    }

    if (!startTime || !endTime) {
      enqueueSnackbar("Invalid time range", { variant: "error" });
      return;
    }

    // 1) Past date check (in organization timezone)
    if (isDateInPast(startTime, orgTimeZone)) {
      enqueueSnackbar("Cannot book in the past", { variant: "error" });
      return;
    }

    // 2) endTime must be after startTime
    if (endTime <= startTime) {
      enqueueSnackbar("End time must be after start time", { variant: "error" });
      return;
    }

    // 3) Check if user-specified times are within orgStart–orgEnd (in organization timezone)
    const [orgSh, orgSm] = orgStart.split(":").map(Number);
    const [orgEh, orgEm] = orgEnd.split(":").map(Number);

    // Convert selected times to organization timezone for validation
    const startInOrgTz = moment.tz(startTime, orgTimeZone);
    const endInOrgTz = moment.tz(endTime, orgTimeZone);

    // Create min/max allowed times for that day in organization timezone
    const orgMin = startInOrgTz.clone().set({ hour: orgSh, minute: orgSm, second: 0, millisecond: 0 });
    const orgMax = startInOrgTz.clone().set({ hour: orgEh, minute: orgEm, second: 0, millisecond: 0 });

    if (startInOrgTz.isBefore(orgMin) || endInOrgTz.isAfter(orgMax)) {
      enqueueSnackbar(
        `Time must be within organization's allowed range: ${orgStart} - ${orgEnd} (${orgTimeZone})`,
        { variant: "error" }
      );
      return;
    }

    // 4) Overlap check (in organization timezone)
    if (hasOverlap(bookings, startTime, endTime, orgTimeZone)) {
      enqueueSnackbar("This time range is already booked", { variant: "error" });
      return;
    }

    // 5) createBooking
    try {
      await createBooking({
        roomId,
        userId,
        startTime,
        endTime,
      });
      enqueueSnackbar("Booking created successfully", { variant: "success" });
      fetchBookings();
      closeDialog();
    } catch (err) {
      console.error("Booking error:", err);
      enqueueSnackbar("Error creating booking", { variant: "error" });
    }
  }

  // FullCalendar config
  const initialView = isMobile ? "timeGridDay" : "timeGridWeek";
  const headerToolbar = isMobile
    ? { left: "prev,next", center: "title", right: "" }
    : {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
      };
  const aspectRatioValue = isMobile ? 0.8 : 1.35;

  return (
    <Box sx={{ mt: 2, border: "1px solid #ccc", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h6">
          Room Calendar
        </Typography>
        <Chip 
          label={`Times shown in ${orgTimeZone} (${moment.tz(orgTimeZone).format('Z')})`} 
          size="small" 
          variant="outlined" 
          color="info"
        />
      </Box>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
        initialView={initialView}
        headerToolbar={headerToolbar}
        timeZone={orgTimeZone} // FullCalendar with luxon will properly handle timezone conversion
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        selectable={true}
        selectMirror={true}
        select={handleSelect}
        eventClick={handleEventClick}
        editable={true} // Enable drag and drop
        eventDrop={handleEventDrop} // Handle drag and drop
        eventResize={handleEventResize} // Handle resize
        longPressDelay={200}
        selectLongPressDelay={200}
        nowIndicator={true} // Should now work correctly with luxon timezone support
        height="auto"
        aspectRatio={aspectRatioValue}
        events={[]} // loaded in fetchBookings
      />

      {/* Dialog for finalizing booking */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Booking</DialogTitle>
        <DialogContent>
          {selectedRange && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Adjust start/end times if needed:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Start Time"
                    ampm={false}
                    value={startTime}
                    onChange={(v) => setStartTime(v)}
                    slots={{
                      textField: (params) => <TextField {...params} />
                    }}
                  />
                  <TimePicker
                    label="End Time"
                    ampm={false}
                    value={endTime}
                    onChange={(v) => setEndTime(v)}
                    slots={{
                      textField: (params) => <TextField {...params} />
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Table size="small" sx={{ mb: 1 }}>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell>
                      {startTime
                        ? moment.tz(startTime, orgTimeZone).format("MMM DD, YYYY")
                        : "--"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Start</strong></TableCell>
                    <TableCell>
                      {startTime
                        ? moment.tz(startTime, orgTimeZone).format("HH:mm")
                        : "--"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>End</strong></TableCell>
                    <TableCell>
                      {endTime
                        ? moment.tz(endTime, orgTimeZone).format("HH:mm")
                        : "--"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        Allowed range: {orgStart} - {orgEnd} ({orgTimeZone})
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmBooking}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={closeEditModal}
        booking={selectedBooking}
        onSave={handleEditBooking}
        organizationWorkHours={{ start: orgStart, end: orgEnd }}
      />

      {/* Action Selection Dialog */}
      <Dialog open={actionDialogOpen} onClose={closeActionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>What would you like to do?</DialogTitle>
        <DialogContent>
          {selectedBookingForAction && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Room:</strong> {selectedBookingForAction.room?.name || 'Meeting Room'}<br/>
                <strong>Date:</strong> {moment.tz(selectedBookingForAction.startTime, orgTimeZone).format('MMM DD, YYYY')}<br/>
                <strong>Time:</strong> {moment.tz(selectedBookingForAction.startTime, orgTimeZone).format('h:mm A')} - {moment.tz(selectedBookingForAction.endTime, orgTimeZone).format('h:mm A')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={handleEditBookingAction}
                  fullWidth
                >
                  ✏️ Edit Booking
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleAddToCalendarAction}
                  fullWidth
                >
                  📅 Add to Calendar
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Integration Dialog */}
      <Dialog open={calendarDialogOpen} onClose={closeCalendarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add to Calendar</DialogTitle>
        <DialogContent>
          {selectedBookingForCalendar && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Add this booking to your calendar:
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Room:</strong> {selectedBookingForCalendar.room?.name || 'Meeting Room'}<br/>
                <strong>Date:</strong> {moment.tz(selectedBookingForCalendar.startTime, orgTimeZone).format('MMM DD, YYYY')}<br/>
                <strong>Time:</strong> {moment.tz(selectedBookingForCalendar.startTime, orgTimeZone).format('h:mm A')} - {moment.tz(selectedBookingForCalendar.endTime, orgTimeZone).format('h:mm A')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" onClick={handleAddToGoogleCalendar} fullWidth>
                  📅 Google Calendar
                </Button>
                <Button variant="outlined" onClick={handleAddToOutlookCalendar} fullWidth>
                  📘 Outlook Calendar
                </Button>
                <Button variant="outlined" onClick={handleAddToYahooCalendar} fullWidth>
                  🟣 Yahoo Calendar
                </Button>
                <Button variant="outlined" onClick={handleAddToAppleCalendar} fullWidth>
                  🍎 Apple Calendar
                </Button>
                <Button variant="outlined" onClick={handleDownloadICS} fullWidth>
                  📄 Download .ics file
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCalendarDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MeetingRoomCalendar;